import React from 'react'
import styled from 'styled-components'
import { colors } from '../styleGuide'

export interface DcSwitchTabOption {
  value: string
  label: React.ReactNode
  disabled?: boolean
}

export interface DcSwitchTabProps {
  /** Array of tab options with value and label */
  tabs: DcSwitchTabOption[]
  /** Currently active tab value */
  value: string
  /** Called when a tab is selected */
  onChange: (value: string) => void
  /** Width of the switch container. Default: 100% */
  width?: string
  /** Optional class name */
  className?: string
  /** Optional inline styles for the container */
  style?: React.CSSProperties
  /** Background color of the track */
  trackBackgroundColor?: string
  /** Border radius of the track (e.g. 10px, 9999px) */
  trackBorderRadius?: string
  /** Border of the track */
  trackBorder?: string
  /** Padding of the track */
  trackPadding?: string
  /** Margin of the track */
  trackMargin?: string
  /** Background color of the active tab */
  activeBackgroundColor?: string
  /** Text color of the active tab */
  activeColor?: string
  /** Text color of the inactive tab */
  inactiveColor?: string
  /** Border radius of each tab button */
  tabBorderRadius?: string
  /** Font size of tab labels */
  tabFontSize?: string
  /** Font weight of tab labels */
  tabFontWeight?: string
  /** Padding of each tab button */
  tabPadding?: string
}

export const DcSwitchTab = React.forwardRef<HTMLDivElement, DcSwitchTabProps>(
  (
    {
      tabs,
      value,
      onChange,
      width = '100%',
      className,
      style,
      trackBackgroundColor = colors.grayLightest,
      trackBorderRadius = '10px',
      trackBorder = `1px solid ${colors.grayBorderLight}`,
      trackPadding = '4px',
      trackMargin,
      activeBackgroundColor = colors.primary,
      activeColor = colors.white,
      inactiveColor = colors.grayTextDark,
      tabBorderRadius = '10px',
      tabFontSize = '0.875rem',
      tabFontWeight = '500',
      tabPadding = '8px 12px',
    },
    ref,
  ) => {
    return (
      <SwitchContainer
        ref={ref}
        className={className}
        style={style}
        width={width}
        trackBackgroundColor={trackBackgroundColor}
        trackBorderRadius={trackBorderRadius}
        trackBorder={trackBorder}
        trackPadding={trackPadding}
        trackMargin={trackMargin}
      >
        {tabs.map((tab) => (
          <TabButton
            key={tab.value}
            type="button"
            active={value === tab.value}
            disabled={!!tab.disabled}
            onClick={() => {
              if (!tab.disabled) {
                onChange(tab.value)
              }
            }}
            activeBackgroundColor={activeBackgroundColor}
            activeColor={activeColor}
            inactiveColor={inactiveColor}
            tabBorderRadius={tabBorderRadius}
            tabFontSize={tabFontSize}
            tabFontWeight={tabFontWeight}
            tabPadding={tabPadding}
          >
            {tab.label}
          </TabButton>
        ))}
      </SwitchContainer>
    )
  },
)

DcSwitchTab.displayName = 'DcSwitchTab'

interface SwitchContainerProps {
  width: string
  trackBackgroundColor: string
  trackBorderRadius: string
  trackBorder: string
  trackPadding: string
  trackMargin?: string
}

const SwitchContainer = styled.div<SwitchContainerProps>`
  display: flex;
  width: ${(props) => props.width};
  background-color: ${(props) => props.trackBackgroundColor};
  border-radius: ${(props) => props.trackBorderRadius};
  border: ${(props) => props.trackBorder};
  padding: ${(props) => props.trackPadding};
  margin: ${(props) => props.trackMargin || '0'};
`

interface TabButtonProps {
  active: boolean
  disabled: boolean
  activeBackgroundColor: string
  activeColor: string
  inactiveColor: string
  tabBorderRadius: string
  tabFontSize: string
  tabFontWeight: string
  tabPadding: string
}

const TabButton = styled.button<TabButtonProps>`
  flex: 1;
  border: none;
  outline: none;
  cursor: ${(props) => (props.disabled ? 'not-allowed' : 'pointer')};
  background-color: ${(props) =>
    props.active ? props.activeBackgroundColor : 'transparent'};
  color: ${(props) => (props.active ? props.activeColor : props.inactiveColor)};
  border-radius: ${(props) => props.tabBorderRadius};
  font-size: ${(props) => props.tabFontSize};
  font-weight: ${(props) => props.tabFontWeight};
  padding: ${(props) => props.tabPadding};
  transition: background-color 0.15s ease-in-out, color 0.15s ease-in-out;
  opacity: ${(props) => (props.disabled ? 0.6 : 1)};
`

export default DcSwitchTab
